import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

// Add calculation helper functions
export const calculateBuilderCost = (quantity, unitCost) => {
  return (Number(quantity) || 0) * (Number(unitCost) || 0);
};

export const calculateClientPrice = (builderCost, markup) => {
  return (Number(builderCost) || 0) * (1 + ((Number(markup) || 0) / 100));
};

export const calculateMargin = (builderCost, clientPrice) => {
  if (!clientPrice || clientPrice <= 0) return 0;
  return ((clientPrice - builderCost) / clientPrice) * 100;
};

export const calculateMarkup = (clientPrice, builderCost) => {
  if (!builderCost || builderCost <= 0) return 0;
  return ((clientPrice / builderCost) - 1) * 100;
};

// Add validation helper
const validateChangeOrderData = (changeOrderData) => {
  const errors = [];
  
  if (!changeOrderData.category) {
    errors.push('Category is required');
  }
  
  if (!changeOrderData.emailAddress) {
    errors.push('Email address is required');
  }
  
  return errors;
};

// Create change order
export const createChangeOrder = createAsyncThunk(
  'changeOrders/createChangeOrder',
  async ({ projectId, changeOrderData }, { rejectWithValue }) => {
    try {
      // Validate change order data
      const validationErrors = validateChangeOrderData(changeOrderData);
      if (validationErrors.length > 0) {
        return rejectWithValue(validationErrors.join(', '));
      }

      // Set default display settings if not provided
      const defaultDisplaySettings = {
        showScopeDescription: false,
        showCostCodeDescription: false,
        showQuantity: false,
        showUnit: false,
        showPrice: false,
        showCostCodes: false,
        expandGroups: false,
        separateGroups: false
      };

      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...changeOrderData,
          project: projectId,
          displaySettings: changeOrderData.displaySettings || defaultDisplaySettings
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to create change order');
      }

      return {
        changeOrder: {
          ...data.changeOrder,
          items: data.changeOrder.items || [],
          displaySettings: data.changeOrder.displaySettings || defaultDisplaySettings
        }
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Fetch change orders for a project
export const fetchChangeOrders = createAsyncThunk(
  'changeOrders/fetchChangeOrders',
  async (projectId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch change orders');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this action creator
export const updateChangeOrder = createAsyncThunk(
  'changeOrders/updateChangeOrder',
  async ({ projectId, changeOrderId, updates }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update change order');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Update change order display settings
export const updateChangeOrderDisplaySettings = createAsyncThunk(
  'changeOrders/updateChangeOrderDisplaySettings',
  async ({ projectId, changeOrderId, displaySettings }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}/display-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ displaySettings })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update display settings');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const deleteChangeOrderItem = createAsyncThunk(
  'changeOrders/deleteChangeOrderItem',
  async ({ changeOrderId, itemId, itemType }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/${changeOrderId}/items`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          itemId, 
          itemType
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete item');
      }

      return {
        changeOrder: data.changeOrder,
        deletedItemId: itemId,
        itemType
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this action if not already present
export const updateChangeOrderStatus = createAsyncThunk(
  'changeOrders/updateStatus',
  async ({ projectId, changeOrderId, status }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update change order status');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this action creator
export const deleteChangeOrder = createAsyncThunk(
  'changeOrders/deleteChangeOrder',
  async ({ projectId, changeOrderId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to delete change order');
      }

      return { changeOrderId };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchChangeOrder = createAsyncThunk(
  'changeOrders/fetchChangeOrder',
  async ({ projectId, changeOrderId }, { rejectWithValue }) => {
    try {
      if (changeOrderId) {
        const response = await fetch(
          `${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        const data = await response.json();

        if (!response.ok) {
          return rejectWithValue(data.message || 'Failed to fetch change order');
        }

        return data;
      }
      return { changeOrder: null };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add these new action creators
export const approveChangeOrder = createAsyncThunk(
  'changeOrders/approveChangeOrder',
  async ({ id, signatureData }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/change-orders/${id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(signatureData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to approve change order');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const rejectChangeOrder = createAsyncThunk(
  'changeOrders/rejectChangeOrder',
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/change-orders/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to reject change order');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add new thunk for manual approval of change order
export const manualApproveChangeOrder = createAsyncThunk(
  'changeOrders/manualApproveChangeOrder',
  async ({ projectId, changeOrderId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}/manual-approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to manually approve change order');
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add these new action creators
export const fetchChangeOrderCostGroups = createAsyncThunk(
  'changeOrders/fetchChangeOrderCostGroups',
  async (changeOrderId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/${changeOrderId}/cost-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.costGroups;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteChangeOrderCostGroup = createAsyncThunk(
  'changeOrders/deleteChangeOrderCostGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/cost-groups/${groupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return { groupId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCostCodesData = createAsyncThunk(
  'changeOrders/fetchCostCodesData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/cost-codes-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.costCodesData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchChangeOrderCostItemsByChangeOrder = createAsyncThunk(
  'changeOrders/fetchChangeOrderCostItemsByChangeOrder',
  async (changeOrderId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/${changeOrderId}/cost-items`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data.costItems;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createChangeOrderCostGroup = createAsyncThunk(
  'changeOrders/createCostGroup',
  async (groupData, { rejectWithValue }) => {
    try {
      const { projectId, changeOrderId, selectedCostCodes = [], ...data } = groupData;
      
      if (!projectId || !changeOrderId) {
        return rejectWithValue('Project ID and Change Order ID are required');
      }

      const token = localStorage.getItem('token');

      // Ensure selectedCostCodes is an array
      const costCodesToSend = Array.isArray(selectedCostCodes) ? selectedCostCodes : [];

      const requestBody = {
        ...data,
        selectedCostCodes: costCodesToSend
      };
      
      console.log('Sending request to create cost group:', JSON.stringify(requestBody));

      // Create the cost group
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}/scope`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const responseData = await response.json();
      if (!response.ok) {
        console.error('Failed to create cost group:', responseData);
        return rejectWithValue(responseData.message || 'Failed to create cost group');
      }

      console.log('Received cost group response:', JSON.stringify(responseData));
      return responseData;
    } catch (error) {
      console.error('Error creating cost group:', error);
      return rejectWithValue(error.message || 'Failed to create cost group');
    }
  }
);

export const createChangeOrderCostItem = createAsyncThunk(
  'changeOrders/createChangeOrderCostItem',
  async ({ projectId, changeOrderId, ...costItemData }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}/cost-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(costItemData)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteChangeOrderCostItem = createAsyncThunk(
  'changeOrders/deleteChangeOrderCostItem',
  async (itemId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/cost-items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message);
      }

      return { itemId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add action to update a change order cost item directly
export const updateChangeOrderCostItem = createAsyncThunk(
  'changeOrders/updateChangeOrderCostItem',
  async ({ itemId, updates }, { rejectWithValue }) => {
    try {
      console.log(`Updating cost item ${itemId} with:`, updates);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/cost-items/${itemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to update cost item');
      }

      return { costItem: data.costItem };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add these actions to fetch the dropdown data
export const fetchChangeOrderCostGroupsData = createAsyncThunk(
  'changeOrders/fetchChangeOrderCostGroupsData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-groups-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost groups data');
      }

      return data.costGroupsData;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

export const fetchChangeOrderCostCodesData = createAsyncThunk(
  'changeOrders/fetchChangeOrderCostCodesData',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/proposals/cost-codes-data`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to fetch cost codes data');
      }

      return data.costCodesData;
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add this function
export const generateChangeOrderTemplate = async (template, data) => {
  try {
    const response = await fetch(`${config.apiUrl}/api/change-orders/generate-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({
        template,
        data
      })
    });

    if (!response.ok) {
      throw new Error('Failed to generate template');
    }

    const result = await response.json();
    return result.html;
  } catch (error) {
    console.error('Error generating template:', error);
    throw error;
  }
};

// Add this action creator
export const releaseChangeOrder = createAsyncThunk(
  'changeOrders/releaseChangeOrder',
  async ({ projectId, changeOrderId }, { rejectWithValue }) => {
    try {
      if (!projectId || !changeOrderId) {
        return rejectWithValue('Project ID and Change Order ID are required');
      }

      console.log(`Releasing change order: ${changeOrderId} for project: ${projectId}`);

      // 1. First, fetch the current change order to validate it has all required data
      const token = localStorage.getItem('token');
      const fetchResponse = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const fetchData = await fetchResponse.json();
      
      if (!fetchResponse.ok) {
        console.error('Error fetching change order before release:', fetchData);
        return rejectWithValue(fetchData.message || 'Failed to fetch change order details');
      }

      if (!fetchData.changeOrder) {
        console.error('Change order not found in response:', fetchData);
        return rejectWithValue('Change order not found');
      }

      const changeOrder = fetchData.changeOrder;

      // 2. Validate the change order data is complete
      if (!changeOrder.category || (typeof changeOrder.category === 'object' && !changeOrder.category._id)) {
        console.error('Change order missing category:', changeOrder);
        return rejectWithValue('Change order is missing category information');
      }

      if (!changeOrder.clientPrice && changeOrder.clientPrice !== 0) {
        console.error('Change order missing clientPrice:', changeOrder);
        return rejectWithValue('Change order is missing price information');
      }

      // 3. Now proceed with the release
      console.log('Sending release request for validated change order');
      const releaseResponse = await fetch(`${config.apiUrl}/api/projects/${projectId}/change-orders/${changeOrderId}/release`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await releaseResponse.json();
      
      if (!releaseResponse.ok) {
        console.error('Error releasing change order:', data);
        return rejectWithValue(data.message || 'Failed to release change order');
      }

      // 4. Validate the response contains the expected data
      if (!data.changeOrder || !data.changeOrder._id) {
        console.error('Invalid response after release:', data);
        return rejectWithValue('Invalid server response: Missing change order data');
      }

      console.log('Successfully released change order:', data.changeOrder);

      // Return the full response with an explicit status update
      return {
        changeOrder: {
          ...data.changeOrder,
          status: 'released' // Ensure status is updated even if the server response doesn't include it
        }
      };
    } catch (error) {
      console.error('Error in releaseChangeOrder:', error);
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

// Add a new thunk for removing cost codes from a scope
export const removeChangeOrderCostCode = createAsyncThunk(
  'changeOrders/removeChangeOrderCostCode',
  async ({ changeOrderId, scopeId, costCodeId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiUrl}/api/change-orders/${changeOrderId}/scope/${scopeId}/cost-codes/${costCodeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data.message || 'Failed to remove cost code');
      }

      return {
        changeOrder: data.changeOrder,
        scopeId,
        costCodeId
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Network error occurred');
    }
  }
);

const changeOrdersSlice = createSlice({
  name: 'changeOrders',
  initialState: {
    changeOrders: [],
    currentChangeOrder: null,
    loading: false,
    error: null,
    costGroupsData: [],
    costCodesData: []
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentChangeOrder: (state, action) => {
      state.currentChangeOrder = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createChangeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChangeOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.changeOrders.push(action.payload.changeOrder);
        state.currentChangeOrder = action.payload.changeOrder;
        showToast('Change order created successfully', 'success');
      })
      .addCase(createChangeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(fetchChangeOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChangeOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.changeOrders = action.payload.changeOrders;
      })
      .addCase(fetchChangeOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateChangeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChangeOrder.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.changeOrders.findIndex(co => co._id === action.payload.changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = action.payload.changeOrder;
        }
        showToast('Change order updated successfully', 'success');
      })
      .addCase(updateChangeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(updateChangeOrderDisplaySettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChangeOrderDisplaySettings.fulfilled, (state, action) => {
        state.loading = false;
        const { changeOrder } = action.payload;
        const index = state.changeOrders.findIndex(co => co._id === changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = {
            ...state.changeOrders[index],
            displaySettings: changeOrder.displaySettings
          };
        }
        if (state.currentChangeOrder && state.currentChangeOrder._id === changeOrder._id) {
          state.currentChangeOrder = {
            ...state.currentChangeOrder,
            displaySettings: changeOrder.displaySettings
          };
        }
      })
      .addCase(updateChangeOrderDisplaySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(deleteChangeOrderItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteChangeOrderItem.fulfilled, (state, action) => {
        const { changeOrder } = action.payload;
        
        // Update the specific change order in the list
        const index = state.changeOrders.findIndex(co => co._id === changeOrder._id);
        if (index !== -1) {
          // Preserve the category when updating the change order
          const existingCategory = state.changeOrders[index].category;
          state.changeOrders[index] = {
            ...changeOrder,
            category: existingCategory || changeOrder.category
          };
        }
        
        // Update the current change order if it matches
        if (state.currentChangeOrder?._id === changeOrder._id) {
          // Preserve the category when updating current change order
          const existingCategory = state.currentChangeOrder.category;
          state.currentChangeOrder = {
            ...changeOrder,
            category: existingCategory || changeOrder.category
          };
        }
        
        state.loading = false;
        state.error = null;
        showToast('Item deleted successfully', 'success');
      })
      .addCase(deleteChangeOrderItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload || 'Failed to delete item', 'error');
      })
      .addCase(updateChangeOrderStatus.fulfilled, (state, action) => {
        const index = state.changeOrders.findIndex(co => co._id === action.payload.changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = action.payload.changeOrder;
        }
        showToast('Change order status updated successfully', 'success');
      })
      .addCase(deleteChangeOrder.fulfilled, (state, action) => {
        state.changeOrders = state.changeOrders.filter(co => co._id !== action.payload.changeOrderId);
        showToast('Change order deleted successfully', 'success');
      })
      .addCase(fetchChangeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChangeOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChangeOrder = action.payload.changeOrder;
      })
      .addCase(fetchChangeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchChangeOrderCostGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChangeOrder = {
          ...state.currentChangeOrder,
          costGroups: action.payload
        };
      })
      .addCase(deleteChangeOrderCostGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChangeOrder = {
          ...state.currentChangeOrder,
          costGroups: state.currentChangeOrder.costGroups.filter(cg => cg._id !== action.payload.groupId)
        };
        showToast('Cost group deleted successfully', 'success');
      })
      .addCase(fetchCostCodesData.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChangeOrder = {
          ...state.currentChangeOrder,
          costCodesData: action.payload
        };
      })
      .addCase(fetchChangeOrderCostItemsByChangeOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChangeOrder = {
          ...state.currentChangeOrder,
          costItems: action.payload
        };
      })
      .addCase(createChangeOrderCostGroup.fulfilled, (state, action) => {
        console.log("Processing createChangeOrderCostGroup.fulfilled:", action.payload);
        if (state.currentChangeOrder) {
          // Add the scope of work with its cost group properly
          if (!state.currentChangeOrder.scopeOfWork) {
            state.currentChangeOrder.scopeOfWork = [];
          }
          
          // Handle the updated response structure where data is in costGroup field
          const costGroup = action.payload.costGroup;
          
          if (costGroup) {
            const scopeEntry = {
              costGroup: costGroup,
              quantity: 1,
              unit: 'EA',
              builderCost: costGroup.builderCost,
              markup: costGroup.markup,
              clientPrice: costGroup.clientPrice,
              margin: costGroup.margin,
              _id: costGroup._id
            };
            
            console.log("Adding scope entry to state:", scopeEntry);
            state.currentChangeOrder.scopeOfWork.push(scopeEntry);
          }
        }
        showToast('Cost group added successfully', 'success');
      })
      .addCase(createChangeOrderCostItem.fulfilled, (state, action) => {
        if (state.currentChangeOrder) {
          state.currentChangeOrder.costCodes = [
            ...(state.currentChangeOrder.costCodes || []),
            action.payload.costItem
          ];
        }
        showToast('Cost item added successfully', 'success');
      })
      .addCase(deleteChangeOrderCostItem.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentChangeOrder?.costItems) {
          state.currentChangeOrder.costItems = state.currentChangeOrder.costItems.filter(
            item => item._id !== action.payload.itemId
          );
        }
        showToast('Cost item deleted successfully', 'success');
      })
      .addCase(fetchChangeOrderCostGroupsData.fulfilled, (state, action) => {
        state.costGroupsData = action.payload;
      })
      .addCase(updateChangeOrderCostItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChangeOrderCostItem.fulfilled, (state, action) => {
        state.loading = false;
        const updatedCostItem = action.payload.costItem;
        
        // Update the cost item in the current change order if it exists
        if (state.currentChangeOrder) {
          // Update in costCodes array (standalone cost items)
          if (state.currentChangeOrder.costCodes) {
            state.currentChangeOrder.costCodes = state.currentChangeOrder.costCodes.map(code => {
              if (code.costItem && code.costItem._id === updatedCostItem._id) {
                return {
                  ...code,
                  costItem: updatedCostItem
                };
              }
              return code;
            });
          }
          
          // Update in scopeOfWork.costCodes arrays (cost items within scope groups)
          if (state.currentChangeOrder.scopeOfWork) {
            state.currentChangeOrder.scopeOfWork = state.currentChangeOrder.scopeOfWork.map(scope => {
              if (scope.costCodes && Array.isArray(scope.costCodes)) {
                return {
                  ...scope,
                  costCodes: scope.costCodes.map(code => {
                    if (code._id === updatedCostItem._id) {
                      return updatedCostItem;
                    }
                    return code;
                  })
                };
              }
              return scope;
            });
          }
        }
        
        showToast('Cost item updated successfully', 'success');
      })
      .addCase(updateChangeOrderCostItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload || 'Failed to update cost item', 'error');
      })
      .addCase(fetchChangeOrderCostCodesData.fulfilled, (state, action) => {
        state.costCodesData = action.payload;
      })
      .addCase(releaseChangeOrder.fulfilled, (state, action) => {
        const index = state.changeOrders.findIndex(co => co._id === action.payload.changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = action.payload.changeOrder;
        }
        state.currentChangeOrder = action.payload.changeOrder;
        showToast('Change order released successfully', 'success');
      })
      .addCase(approveChangeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveChangeOrder.fulfilled, (state, action) => {
        const index = state.changeOrders.findIndex(co => co._id === action.payload.changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = action.payload.changeOrder;
        }
        state.currentChangeOrder = action.payload.changeOrder;
        state.loading = false;
        showToast('Change order approved successfully', 'success');
      })
      .addCase(approveChangeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(rejectChangeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectChangeOrder.fulfilled, (state, action) => {
        const index = state.changeOrders.findIndex(co => co._id === action.payload.changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = action.payload.changeOrder;
        }
        state.currentChangeOrder = action.payload.changeOrder;
        state.loading = false;
        showToast('Change order rejected successfully', 'success');
      })
      .addCase(rejectChangeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      })
      .addCase(removeChangeOrderCostCode.fulfilled, (state, action) => {
        const { changeOrder } = action.payload;
        // Update the specific change order in the list
        const index = state.changeOrders.findIndex(co => co._id === changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = changeOrder;
        }
        // Update the current change order if it matches
        if (state.currentChangeOrder?._id === changeOrder._id) {
          state.currentChangeOrder = changeOrder;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(removeChangeOrderCostCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(manualApproveChangeOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(manualApproveChangeOrder.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.changeOrders.findIndex(co => co._id === action.payload.changeOrder._id);
        if (index !== -1) {
          state.changeOrders[index] = action.payload.changeOrder;
        }
        state.currentChangeOrder = action.payload.changeOrder;
        showToast('Change order manually approved successfully', 'success');
      })
      .addCase(manualApproveChangeOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        showToast(action.payload, 'error');
      });
  }
});

export const { clearError, setCurrentChangeOrder } = changeOrdersSlice.actions;
export default changeOrdersSlice.reducer; 